if (!customElements.get('product-purchase-options')) {
  customElements.define(
    'product-purchase-options',
    class ProductPurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.data = this.readData();
        this.i18n = this.readI18n();
        this.discountPercent = parseInt(this.dataset.discountPercent || '15', 10);
        this.onetimeDays = parseInt(this.dataset.onetimeDays || '30', 10);
        this.packDays = parseInt(this.dataset.packDays || '90', 10);
        this.currency = this.dataset.currency || 'EUR';
        this.options = this.querySelectorAll('.product-purchase-options__option');
        this.radios = this.querySelectorAll('.product-purchase-options__radio');
        this.activeLanguage = this.getActiveLanguage();

        this.ensureSellingPlanInput();
        this.bindEvents();
        this.applyTranslations(this.activeLanguage);

        if (this.data && this.data.selectedVariantId && this.data.variants) {
          const initialVariant = this.data.variants[String(this.data.selectedVariantId)];
          if (initialVariant) {
            this.updateDisplayedPrices(initialVariant);
          }
        }

        this.syncFromSelection();
        this.observeLanguageChanges();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.sectionId) return;
          this.onVariantChange(event.data.variant);
        });
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.langCheckInterval) {
          clearInterval(this.langCheckInterval);
        }

        if (this.langObserver) {
          this.langObserver.disconnect();
        }
      }

      readData() {
        const node = this.querySelector('[data-purchase-options-data]');
        if (!node) return null;

        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          return null;
        }
      }

      readI18n() {
        const node = this.querySelector('[data-purchase-options-i18n]');
        if (!node) return null;

        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          return null;
        }
      }

      getActiveLanguage() {
        const gtranslateLang = this.getGTranslateLanguage();
        if (gtranslateLang) return gtranslateLang;

        const htmlLang = (document.documentElement.lang || '').split('-')[0].toLowerCase();
        if (this.isSupportedLanguage(htmlLang)) return htmlLang;

        const initialLocale = (this.dataset.initialLocale || '').split('-')[0].toLowerCase();
        if (this.isSupportedLanguage(initialLocale)) return initialLocale;

        const host = window.location.hostname.toLowerCase();
        if (host.startsWith('es.')) return 'es';
        if (host.startsWith('de.')) return 'de';

        return 'en';
      }

      getGTranslateLanguage() {
        const cookies = document.cookie ? document.cookie.split(';') : [];

        for (const cookie of cookies) {
          const trimmed = cookie.trim();
          if (!trimmed.startsWith('googtrans=')) continue;

          const value = decodeURIComponent(trimmed.substring('googtrans='.length));
          const parts = value.split('/').filter(Boolean);
          const target = parts[parts.length - 1];

          if (!target || target === 'auto') continue;

          const lang = target.split('-')[0].toLowerCase();
          if (this.isSupportedLanguage(lang)) return lang;
        }

        return null;
      }

      isSupportedLanguage(lang) {
        return lang === 'en' || lang === 'es' || lang === 'de';
      }

      observeLanguageChanges() {
        let lastLanguage = this.activeLanguage;

        const checkLanguage = () => {
          const nextLanguage = this.getActiveLanguage();
          if (nextLanguage === lastLanguage) return;

          lastLanguage = nextLanguage;
          this.activeLanguage = nextLanguage;
          this.applyTranslations(nextLanguage);

          const variantId = this.data && this.data.selectedVariantId;
          if (variantId && this.data.variants && this.data.variants[String(variantId)]) {
            this.updateDisplayedPrices(this.data.variants[String(variantId)]);
          }
        };

        document.addEventListener('click', (event) => {
          if (event.target.closest('.gt_float_switcher, .gt_switcher, .gtranslate-dropdown, [data-gt-lang]')) {
            setTimeout(checkLanguage, 150);
            setTimeout(checkLanguage, 600);
            setTimeout(checkLanguage, 1200);
          }
        });

        this.langObserver = new MutationObserver(checkLanguage);
        this.langObserver.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['lang', 'class'],
        });

        this.langCheckInterval = setInterval(checkLanguage, 1500);
      }

      applyTranslations(language) {
        if (!this.i18n) return;

        const strings = this.i18n[language] || this.i18n.en;
        if (!strings) return;

        const onetimeLabel = this.querySelector('[data-po-label="onetime"]');
        const packLabel = this.querySelector('[data-po-label="pack"]');
        const legend = this.querySelector('[data-po-legend]');

        const onetimeSavePercent = this.dataset.onetimeSavePercent;
        const onetimeText =
          onetimeSavePercent && strings.labelOnetimeSave
            ? strings.labelOnetimeSave.replace('{percent}', String(onetimeSavePercent))
            : strings.labelOnetime;

        if (onetimeLabel) {
          onetimeLabel.textContent = onetimeText;
        }

        if (packLabel) {
          packLabel.textContent = strings.labelPack.replace('{percent}', String(this.discountPercent));
        }

        if (legend) {
          legend.textContent = `${onetimeText} / ${strings.labelPack.replace('{percent}', String(this.discountPercent))}`;
        }

        this.updatePerDayLabels(language);
      }

      updatePerDayLabels(language) {
        if (!this.i18n) return;

        const strings = this.i18n[language] || this.i18n.en;
        if (!strings) return;

        const onetimeContainer = this.querySelector('[data-po-per-day="onetime"]');
        const packContainer = this.querySelector('[data-po-per-day="pack"]');

        if (onetimeContainer && this.onetimePerDayCents != null) {
          onetimeContainer.textContent = strings.perDay.replace(
            '{price}',
            this.formatMoney(this.onetimePerDayCents, language)
          );
        }

        if (packContainer && this.packPerDayCents != null) {
          packContainer.textContent = strings.perDay.replace(
            '{price}',
            this.formatMoney(this.packPerDayCents, language)
          );
        }
      }

      bindEvents() {
        this.radios.forEach((radio) => {
          radio.addEventListener('change', () => this.syncFromSelection());
        });

        this.options.forEach((option) => {
          option.addEventListener('click', () => {
            const radio = option.querySelector('.product-purchase-options__radio');
            if (!radio || radio.checked) return;
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }

      ensureSellingPlanInput() {
        if (!this.form) return;

        this.sellingPlanInput = this.form.querySelector('[name="selling_plan"]');

        if (!this.sellingPlanInput) {
          this.sellingPlanInput = document.createElement('input');
          this.sellingPlanInput.type = 'hidden';
          this.sellingPlanInput.name = 'selling_plan';
          this.sellingPlanInput.className = 'product-purchase-options__selling-plan-input';
          this.form.appendChild(this.sellingPlanInput);
        }
      }

      syncFromSelection() {
        const selected = this.querySelector('.product-purchase-options__radio:checked');
        const purchaseType = selected ? selected.value : 'onetime';

        this.options.forEach((option) => {
          option.classList.toggle(
            'product-purchase-options__option--selected',
            option.dataset.purchaseType === purchaseType
          );
        });

        if (!this.sellingPlanInput) return;

        if (purchaseType === 'pack' && selected && selected.dataset.sellingPlanId) {
          this.sellingPlanInput.value = selected.dataset.sellingPlanId;
          this.sellingPlanInput.disabled = false;
        } else {
          this.sellingPlanInput.value = '';
          this.sellingPlanInput.disabled = true;
        }
      }

      onVariantChange(variant) {
        if (!variant || !this.data || !this.data.variants) return;

        const variantData = this.data.variants[String(variant.id)];
        if (!variantData) return;

        this.data.selectedVariantId = variant.id;
        this.updateDisplayedPrices(variantData);

        const packRadio = this.querySelector('[data-purchase-type="pack"] .product-purchase-options__radio');
        if (packRadio) {
          const planId = this.getThreeMonthPlanId(variantData);
          if (planId) {
            packRadio.dataset.sellingPlanId = planId;
            packRadio.disabled = false;
            packRadio.closest('.product-purchase-options__option').hidden = false;
          } else {
            packRadio.disabled = true;
            packRadio.closest('.product-purchase-options__option').hidden = true;
            const onetimeRadio = this.querySelector('[data-purchase-type="onetime"] .product-purchase-options__radio');
            if (onetimeRadio) {
              onetimeRadio.checked = true;
            }
          }
        }

        this.syncFromSelection();
      }

      getThreeMonthPlanId(variantData) {
        const configured = this.dataset.configuredPlanId || (this.data && this.data.configuredPlanId) || '';

        if (configured && variantData.sellingPlans && variantData.sellingPlans[configured]) {
          return configured;
        }

        if (variantData.threeMonthPlanId) {
          return String(variantData.threeMonthPlanId);
        }

        if (this.data && this.data.threeMonthPlanId) {
          return String(this.data.threeMonthPlanId);
        }

        return this.resolveThreeMonthPlanFromNames(variantData);
      }

      resolveThreeMonthPlanFromNames(variantData) {
        if (!variantData || !variantData.sellingPlans) return null;

        const entries = Object.entries(variantData.sellingPlans);
        const isShortTerm = (text) => {
          const value = (text || '').toLowerCase();
          if (/2[\s-]?month|two month|every 2 month|bi-?monthly/.test(value)) return true;
          if (/1[\s-]?month|every month|monthly delivery/.test(value) && !/3[\s-]?month|three month|every 3 month/.test(value)) {
            return true;
          }
          return false;
        };
        const isThreeMonth = (text) => {
          const value = (text || '').toLowerCase();
          return /3[\s-]?month|three month|every 3 month|quarterly|90[\s-]?day/.test(value);
        };

        const strong = entries.find(([, plan]) => plan.name && isThreeMonth(plan.name) && !isShortTerm(plan.name));
        if (strong) return strong[0];

        const loose = entries.find(([, plan]) => {
          const value = (plan.name || '').toLowerCase();
          return value.includes('3') && value.includes('month') && !isShortTerm(value);
        });
        return loose ? loose[0] : null;
      }

      updateDisplayedPrices(variantData) {
        const language = this.activeLanguage || this.getActiveLanguage();

        if (variantData.fixedDisplay) {
          this.onetimePerDayCents = variantData.onetimePerDay;
          this.packPerDayCents = variantData.packPerDay;
          this.setText('[data-onetime-compare-price]', this.formatMoney(variantData.onetimeComparePrice, language));
          this.setText('[data-onetime-price]', this.formatMoney(variantData.price, language));
          this.setText('[data-pack-compare-price]', this.formatMoney(variantData.packFullPrice, language));
          this.setText('[data-pack-price]', this.formatMoney(variantData.packDiscountedPrice, language));
          this.updatePerDayLabels(language);
          return;
        }

        const onetimePrice = variantData.price;
        const packFullPrice = this.getPackFullPrice(variantData, onetimePrice);
        const packDiscountedPrice = this.getPackDiscountedPrice(variantData, packFullPrice);
        const onetimePerDay = Math.round(onetimePrice / this.onetimeDays);
        const packPerDay = Math.round(packDiscountedPrice / this.packDays);

        this.onetimePerDayCents = onetimePerDay;
        this.packPerDayCents = packPerDay;

        this.setText('[data-onetime-price]', this.formatMoney(onetimePrice, language));
        this.setText('[data-pack-compare-price]', this.formatMoney(packFullPrice, language));
        this.setText('[data-pack-price]', this.formatMoney(packDiscountedPrice, language));
        this.updatePerDayLabels(language);
      }

      getPackFullPrice(variantData, onetimePrice) {
        const planId = this.getThreeMonthPlanId(variantData);
        const planData = planId ? variantData.sellingPlans[String(planId)] : null;

        if (planData && planData.compareAtPrice > 0) {
          return planData.compareAtPrice;
        }

        return variantData.packFullPrice || onetimePrice * 3;
      }

      getPackDiscountedPrice(variantData, packFullPrice) {
        const planId = this.getThreeMonthPlanId(variantData);
        const planData = planId ? variantData.sellingPlans[String(planId)] : null;

        if (planData && planData.price > 0) {
          return planData.price;
        }

        if (variantData.packDiscountedPrice) {
          return variantData.packDiscountedPrice;
        }

        return Math.round((packFullPrice * (100 - this.discountPercent)) / 100);
      }

      setText(selector, value) {
        const node = this.querySelector(selector);
        if (node) {
          node.textContent = value;
        }
      }

      formatMoney(cents, language = 'en') {
        const localeMap = {
          en: 'en-IE',
          es: 'es-ES',
          de: 'de-DE',
        };
        const locale = localeMap[language] || localeMap.en;

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch (error) {
          if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            const format =
              (window.theme && window.theme.moneyFormat) ||
              window.Shopify.money_format ||
              '€{{amount}}';

            return window.Shopify.formatMoney(cents, format);
          }

          return `€${(cents / 100).toFixed(2)}`;
        }
      }
    }
  );
}
